/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { CANCEL_TEXT } from '../../../Constants/strings';
import { useAuth } from '../../../Hooks/useAuth';
import UseAxios from '../../../Api/http/http';
import {
  createDeliveryMethodology,
  deliveryMethodologyListRequest,
  updateDeliveryMethodology,
} from '../../../Api/request/deliveryMethodology';
import FormDialog from '../../../Components/Modal/modal';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { deliveryMethodologyTableConfig } from './DeliveryMethodologyConfig';
import './DeliveryMethodology.scss';

const INITIAL_METHODOLOGY_FORM = {
  methodology: '',
};

const getAxiosResultMessage = (result) => (
  result?.data?.response?.data?.message
  || result?.data?.response?.data?.externalMessage
  || result?.data?.message
  || ''
);

const isSuccessfulAxiosResult = (result) => (
  Number.isInteger(result?.status) && result.status >= 200 && result.status < 300
);

const renderMethodologyModal = ({
  dialogMode,
  methodologyForm,
  fieldErrors,
  isSubmitDisabled,
  handleMethodologyFormChange,
  handleCloseMethodologyDialog,
  handleSubmitMethodology,
}) => createPortal(
  <div className="delivery-methodology-modal-backdrop">
    <form className="delivery-methodology-modal" onSubmit={handleSubmitMethodology}>
      <div className="delivery-methodology-modal__header">
        {dialogMode === 'edit' ? 'Edit Delivery Methodology' : 'Add Delivery Methodology'}
      </div>
      <div className="delivery-methodology-modal__body">
        <div className="delivery-methodology-modal__field">
          <label htmlFor="methodology-name">
            Delivery Methodology<span className="required-asterisk">*</span>
          </label>
          <input
            id="methodology-name"
            name="methodology"
            type="text"
            placeholder="Enter Delivery Methodology"
            value={methodologyForm.methodology}
            onChange={handleMethodologyFormChange}
            className={fieldErrors.methodology ? 'is-error' : ''}
          />
        </div>
      </div>
      <div className="delivery-methodology-modal__footer">
        <button
          type="button"
          className="delivery-methodology-modal__btn delivery-methodology-modal__btn--ghost"
          onClick={handleCloseMethodologyDialog}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="delivery-methodology-modal__btn delivery-methodology-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {dialogMode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const buildMethodologyConfig = ({ userProfile, showAddNew, total, onAddClick, hasActionAccess }) => {
  const dynamicTableConfig = deliveryMethodologyTableConfig.filter(col => {
    if (col.field === 'action' && !hasActionAccess) {
      return false;
    }
    return true;
  });

  return {
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {
        emailId: userProfile?.email || '',
        permissions: userProfile?.permissions || {},
        roleDetails: userProfile?.roleDetails || [],
        employeeId: userProfile?.employeeId || '',
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },
      handlers: {
        handleAddIconClick: onAddClick,
      },
      toolBarSettings: {
        showAddNew,
        showFilterOption: false,
        showAppliedFilters: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Delivery Methodology',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: dynamicTableConfig,
      loaderSkeleton: {
        rows: 8,
        bodyCells: 1,
        columns: dynamicTableConfig.length,
      },
      paginationConfig: {
        page: 1,
        limit: Math.max(total || 0, 1000),
        total,
        rowsPerPageOptions: [5, 10, 25, 50],
      },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  };
};

const DeliveryMethodology = () => {
  const { userProfile } = useAuth();

  const permission = userProfile?.permissions?.[MODULE_NAMES.MASTERS_DELIVERY_METHODOLGY]
  || userProfile?.permissions?.['Delivery Methodology'];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );

  const normalizedPermissions = useMemo(
    () => (permission || []).map((action) => String(action).toLowerCase()),
    [permission],
  );

  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
      delete: ['delete', 'remove'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);

  const [methodologies, setMethodologies] = useState([]);
  const [dialogState, setDialogState] = useState({
    mode: 'add',
    selectedMethodology: null,
    isOpen: false,
  });

  const [methodologyForm, setMethodologyForm] = useState(INITIAL_METHODOLOGY_FORM);
  const [fieldErrors, setFieldErrors] = useState({ methodology: false });
  const [isLoading, setIsLoading] = useState(false);
  const [toggleMethodology, setToggleMethodology] = useState(null);

  const loadMethodologies = useCallback(async () => {
    if (!hasAccess('read')) {
      setMethodologies([]);
      return;
    }
    try {
      const response = await UseAxios(deliveryMethodologyListRequest(), setIsLoading);
      if (!isSuccessfulAxiosResult(response)) {
        setMethodologies([]);
        return;
      }
      const rows = response?.data?.deliveryMethodologyList || response?.data?.data?.tableData || [];
      setMethodologies(rows.map((row) => ({
        ...row,
        methodId: row.methodId ?? row.method_id,
        methodology: row.methodology ?? row.methodologyName,
        isActive: row.isActive ?? Boolean(row.is_active),
        status: row.status || ((row.isActive ?? row.is_active) ? 'Active' : 'Inactive'),
      })));
    } catch (_error) {
      setMethodologies([]);
    }
  }, [hasAccess, setIsLoading]);

  useEffect(() => {
    loadMethodologies();
  }, [loadMethodologies]);

  const handleOpenAddMethodology = useCallback(() => {
    setMethodologyForm(INITIAL_METHODOLOGY_FORM);
    setFieldErrors({ methodology: false });
    setDialogState({ mode: 'add', selectedMethodology: null, isOpen: true });
  }, []);

  const handleOpenEditMethodology = useCallback((methodologyItem) => {
    setMethodologyForm({ methodology: methodologyItem.methodology || '' });
    setFieldErrors({ methodology: false });
    setDialogState({ mode: 'edit', selectedMethodology: methodologyItem, isOpen: true });
  }, []);

  const handleCloseMethodologyDialog = useCallback(() => {
    setMethodologyForm(INITIAL_METHODOLOGY_FORM);
    setFieldErrors({ methodology: false });
    setDialogState({ mode: 'add', selectedMethodology: null, isOpen: false });
  }, []);

  const handleMethodologyFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setMethodologyForm((current) => ({ ...current, [name]: value }));
    if (name === 'methodology' && value.trim()) {
      setFieldErrors({ methodology: false });
    }
  }, []);

  const isSubmitDisabled = useMemo(() => {
    if (dialogState.mode !== 'edit' || !dialogState.selectedMethodology) {
      return false;
    }
    return methodologyForm.methodology.trim() === dialogState.selectedMethodology.methodology?.trim();
  }, [dialogState.mode, dialogState.selectedMethodology, methodologyForm.methodology]);

  const handleSubmitMethodology = useCallback(async (event) => {
    event.preventDefault();
    const trimmedMethodologyName = methodologyForm.methodology.trim();

    if (!trimmedMethodologyName) {
      setFieldErrors({ methodology: true });
      toast.error('Please enter delivery methodology.');
      return;
    }

    const request = dialogState.mode === 'edit'
      ? updateDeliveryMethodology(
        dialogState.selectedMethodology.methodId,
        trimmedMethodologyName,
        dialogState.selectedMethodology.isActive ? 1 : 0,
      )
      : createDeliveryMethodology(trimmedMethodologyName);

    const response = await UseAxios(request, setIsLoading);

    if (!isSuccessfulAxiosResult(response)) {
      toast.error(getAxiosResultMessage(response) || 'Failed to save delivery methodology.');
      return;
    }

    toast.success(
      response?.data?.message
        || (dialogState.mode === 'edit'
          ? 'Delivery methodology updated successfully.'
          : 'Delivery methodology added successfully.'),
    );

    handleCloseMethodologyDialog();
    loadMethodologies();
  }, [
    dialogState.mode,
    dialogState.selectedMethodology,
    handleCloseMethodologyDialog,
    loadMethodologies,
    methodologyForm.methodology,
    setIsLoading,
  ]);

  const handleToggleMethodology = useCallback((methodologyItem) => {
    setToggleMethodology(methodologyItem);
  }, []);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleMethodology(null);
  }, []);

  const handleConfirmToggleMethodology = useCallback(async () => {
    if (!toggleMethodology) return;
    const response = await UseAxios(
      updateDeliveryMethodology(
        toggleMethodology.methodId,
        toggleMethodology.methodology,
        toggleMethodology.isActive ? 0 : 1,
      ),
      setIsLoading,
    );

    if (!isSuccessfulAxiosResult(response)) {
      toast.error(getAxiosResultMessage(response) || 'Failed to update delivery methodology status.');
      return;
    }

    toast.success(
      response?.data?.message
        || `Delivery methodology ${toggleMethodology.isActive ? 'disabled' : 'enabled'} successfully.`,
    );

    handleCloseToggleDialog();
    loadMethodologies();
  }, [handleCloseToggleDialog, loadMethodologies, setIsLoading, toggleMethodology]);

  const tableData = useMemo(() => methodologies.map((methodologyItem, index) => ({
    ...methodologyItem,
    slNo: index + 1,
    status: methodologyItem.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="delivery-methodology-actions-cell" onClick={(event) => event.stopPropagation()}>
        <div className="delivery-methodology-action-cell-inner">
          {(hasAccess('update') || hasAccess('delete')) && (
            <ControlledSwitches
              checked={Boolean(methodologyItem.isActive)}
              switchCss="switchCss"
              handleChange={() => handleToggleMethodology(methodologyItem)}
            />
          )}
          {hasAccess('update') && (
            <button
              type="button"
              className="delivery-methodology-action-btn"
              title="Edit Methodology"
              aria-label={`Edit ${methodologyItem.methodology}`}
              onClick={() => handleOpenEditMethodology(methodologyItem)}
            >
              <img src={EditActionIcon} alt="Edit" />
            </button>
          )}
        </div>
      </div>
    ),
  })), [hasAccess, handleOpenEditMethodology, handleToggleMethodology, methodologies]);

  const configuration = useMemo(() => buildMethodologyConfig({
    userProfile,
    showAddNew: hasAccess('create'),
    total: tableData.length,
    onAddClick: handleOpenAddMethodology,
    hasActionAccess: hasAccess('update') || hasAccess('delete'),
  }), [handleOpenAddMethodology, hasAccess, tableData.length, userProfile]);

  if (!hasAccess('read')) {
    return <div>You do not have permission to view delivery methodology list.</div>;
  }

  return (
    <div className="delivery-methodology-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialogState.isOpen && renderMethodologyModal({
        dialogMode: dialogState.mode,
        methodologyForm,
        fieldErrors,
        isSubmitDisabled,
        handleMethodologyFormChange,
        handleCloseMethodologyDialog,
        handleSubmitMethodology,
      })}

      <FormDialog
        open={Boolean(toggleMethodology)}
        title={toggleMethodology?.isActive ? 'Disable Methodology' : 'Enable Methodology'}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {toggleMethodology?.isActive
                ? 'Do you want to disable this methodology?' : 'Do you want to enable this methodology?'}
            </div>
          </div>
        )}
        button1={CANCEL_TEXT}
        cancelButtonId="cancel-methodology-toggle"
        button2={toggleMethodology?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="methodology-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggleMethodology}
      />
    </div>
  );
};

export default DeliveryMethodology;
